import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const pluginKey = new PluginKey('collapsibleHeadings');

/**
 * Build decorations that:
 * 1. Mark collapsed headings with data-collapsed attribute
 * 2. Hide all nodes between a collapsed heading and the next heading of same/higher level
 */
function buildDecorations(doc, collapsed) {
  if (collapsed.size === 0) return DecorationSet.empty;

  const decorations = [];

  // Collect all top-level doc children with positions
  const topNodes = [];
  doc.forEach((node, offset) => {
    topNodes.push({ node, pos: offset });
  });

  collapsed.forEach((collapsedPos) => {
    const idx = topNodes.findIndex((n) => n.pos === collapsedPos);
    if (idx === -1) return;

    const { node: headingNode, pos: headingPos } = topNodes[idx];
    if (headingNode.type.name !== 'heading') return;

    const level = headingNode.attrs.level;

    // Decorate the heading itself
    decorations.push(
      Decoration.node(headingPos, headingPos + headingNode.nodeSize, {
        'data-collapsed': '',
      })
    );

    // Hide subsequent sibling nodes until next heading of same/higher level
    for (let i = idx + 1; i < topNodes.length; i++) {
      const { node, pos } = topNodes[i];
      if (node.type.name === 'heading' && node.attrs.level <= level) break;
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: 'collapsed-content',
          style: 'display: none;',
        })
      );
    }
  });

  return DecorationSet.create(doc, decorations);
}

export const CollapsibleHeadings = Extension.create({
  name: 'collapsibleHeadings',

  addCommands() {
    return {
      clearCollapsed:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(pluginKey, new Set());
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,

        state: {
          init() {
            return new Set();
          },
          apply(tr, oldState) {
            // Explicit meta (toggle or clear)
            const meta = tr.getMeta(pluginKey);
            if (meta !== undefined) return meta;

            // Remap positions when document changes
            if (tr.docChanged) {
              const mapped = new Set();
              oldState.forEach((pos) => {
                const newPos = tr.mapping.map(pos);
                const node = tr.doc.nodeAt(newPos);
                if (node && node.type.name === 'heading') {
                  mapped.add(newPos);
                }
              });
              return mapped;
            }
            return oldState;
          },
        },

        props: {
          decorations(state) {
            const collapsed = pluginKey.getState(state);
            return buildDecorations(state.doc, collapsed);
          },

          handleDOMEvents: {
            mousedown(view, event) {
              const target = event.target;
              if (!target.matches || !target.matches('h1, h2, h3')) return false;

              // Only react to clicks on the chevron (past the end of the text)
              const range = document.createRange();
              range.selectNodeContents(target);
              const textRect = range.getBoundingClientRect();
              if (event.clientX < textRect.right) return false;

              // Resolve the heading position in the doc
              const pos = view.posAtDOM(target, 0);
              const resolved = view.state.doc.resolve(pos);

              let headingPos = null;
              for (let d = resolved.depth; d >= 0; d--) {
                if (resolved.node(d).type.name === 'heading') {
                  headingPos = resolved.before(d);
                  break;
                }
              }
              if (headingPos === null) return false;

              const node = view.state.doc.nodeAt(headingPos);
              if (!node || node.type.name !== 'heading') return false;

              // Toggle
              const collapsed = new Set(pluginKey.getState(view.state));
              if (collapsed.has(headingPos)) {
                collapsed.delete(headingPos);
              } else {
                collapsed.add(headingPos);
              }

              view.dispatch(view.state.tr.setMeta(pluginKey, collapsed));
              event.preventDefault();
              return true;
            },
          },
        },
      }),
    ];
  },
});

export default CollapsibleHeadings;
